import apiClient from '../lib/api';
import { ParsedAuction } from '../contexts';
import {
  normalizeAuctionStatus,
  resolveAuctionPrice,
  resolveEstimatedRetailPrice,
  sanitizeAuctionPhotoUrls,
  isUsableImageUrl,
} from '../lib/auctionPresentation';

// ─── Types ──────────────────────────────────────────────────────────────────
//
// New BE response shape (from Modules/Auctions UI/Http/Responses):
//   AuctionHttpResponse {
//     id: number, name: string, categoryId: number, data: string,
//     startDateUtc: string, status: "Scheduled" | "Active" | "Sold" | ...,
//     currentPrice?: { auctionId, uid, price, currency, asOfUtc }
//   }

export type AuctionDto = {
  id: number;
  name: string;
  categoryId: number;
  data: string; // jsonb blob — title/description/photoUrls/etc
  startDateUtc: string;
  status: 'Scheduled' | 'Active' | 'Sold' | 'Cancelled' | 'Expired';
  currentPrice?: PriceDto | null;
};

export type PriceDto = {
  auctionId: number;
  uid: string;
  price: number;
  currency: string;
  asOfUtc: string;
};

const parseAuctionPayload = (payload: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  } catch (error) {
    console.error('Failed to parse auction payload:', error);
    return {};
  }
};

const pickText = (
  payload: Record<string, unknown>,
  key: string,
  fallback = '',
): string => {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
};

const mapBeStatusToFe = (
  status: AuctionDto['status'],
): 'active' | 'completed' | 'pending' | undefined => {
  switch (status) {
    case 'Active': return 'active';
    case 'Sold':
    case 'Expired':
    case 'Cancelled': return 'completed';
    case 'Scheduled': return 'pending';
    default: return undefined;
  }
};

const normalizeAuction = (auction: AuctionDto): ParsedAuction => {
  const parsedData = parseAuctionPayload(auction.data);
  const title = pickText(parsedData, 'title', auction.name || `Aukce ${auction.id}`);
  const providerDescription = pickText(parsedData, 'providerDescription');
  const partnerLogo = isUsableImageUrl(parsedData.partnerLogo)
    ? parsedData.partnerLogo
    : undefined;

  // Prefer the BE's currentPrice (canonical, includes share-link / drop effects)
  // and fall back to whatever the FE-supplied data blob carries — keeps the
  // legacy mock data path working when REACT_APP_USE_MOCK_DATA is on.
  const livePrice = auction.currentPrice?.price ?? resolveAuctionPrice(parsedData) ?? 0;

  return {
    auctionId: auction.id,
    title,
    description: pickText(parsedData, 'description', 'Detail produktu se připravuje.'),
    price: livePrice,
    estimatedRetailPrice: resolveEstimatedRetailPrice(parsedData),
    photoUrls: sanitizeAuctionPhotoUrls(parsedData.photoUrls, title),
    providerDescription,
    partnerLogo,
    startDate: auction.startDateUtc,
    status: normalizeAuctionStatus(mapBeStatusToFe(auction.status)),
  };
};

/** GET /api/v1/auctions */
export const fetchAuctions = async (): Promise<ParsedAuction[]> => {
  const { data } = await apiClient.get<AuctionDto[]>('/api/v1/auctions');
  return data.map(normalizeAuction);
};

/** GET /api/v1/auctions/{id} */
export const fetchAuctionById = async (auctionId: number): Promise<ParsedAuction> => {
  const { data } = await apiClient.get<AuctionDto>(`/api/v1/auctions/${auctionId}`);
  return normalizeAuction(data);
};

/** GET /api/v1/auctions/{id}/price — current canonical price only. */
export const fetchAuctionPrice = async (auctionId: number): Promise<PriceDto> => {
  const { data } = await apiClient.get<PriceDto>(`/api/v1/auctions/${auctionId}/price`);
  return data;
};

/**
 * Past auctions = anything in a terminal status (Sold / Cancelled / Expired).
 * The legacy "skip first" fallback is gone — BE status is authoritative now.
 */
export const fetchPastAuctions = async (): Promise<ParsedAuction[]> => {
  const all = await fetchAuctions();
  return all.filter((a) => a.status === 'completed').slice(0, 24);
};

/**
 * POST /api/v1/auctions/{id}/drop-price [Authorize] — records the user's
 * desired price (intent only; price flips through ShareLinks or admin action).
 */
export const dropAuctionPrice = async (
  auctionId: number,
  desiredPrice: number,
): Promise<{ recorded: boolean; desiredPrice: number; currentPrice: PriceDto | null }> => {
  const { data } = await apiClient.post(
    `/api/v1/auctions/${auctionId}/drop-price`,
    { desiredPrice },
  );
  return data;
};
